/**
 * Plays a page's tour the first time someone opens it, remembers that on the
 * server (the `toursSeen` preference, so it follows them to any device), and
 * offers it again: "Take the tour" in the top bar and in ⌘K, "Show tours
 * again" in Account.
 *
 * A tour starts only once its first part is on the page — on a page that
 * isn't open yet, or still loading, it waits, and if the part never comes it
 * doesn't play and isn't marked seen. Automated browsers (the tests, the
 * design audit) don't get it uninvited.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { CircleHelp } from 'lucide-react'
import { useEffect, useMemo, useRef } from 'react'
import { orpc } from '@/adapters/rpc/client'
import { type Command, useRegisterCommands } from '@/components/app'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { startTour, useActiveTour } from '../components/tour/tour-store'
import { findTourTarget } from '../components/tour/tour-target'
import { type PageTour, tourFor } from './page-tours'

const SEEN = { input: { key: 'toursSeen' as const } }
/** How long to wait for a page's first part before not playing its tour. */
const WAIT_FOR_MS = 6_000
const LOOK_EVERY_MS = 200

/** The tours this person has been shown, once loaded. */
export function useToursSeen(enabled = true) {
  return useQuery({
    ...orpc.me.preferences.get.queryOptions(SEEN),
    enabled,
    staleTime: 60_000,
    retry: false,
    select: (data) => (Array.isArray(data.value) ? (data.value as string[]) : []),
  })
}

export function useSaveToursSeen() {
  const queryClient = useQueryClient()
  return useMutation(
    orpc.me.preferences.set.mutationOptions({
      // Only this person's own list changed, and this is where it's read.
      onSuccess: (saved) => {
        queryClient.setQueryData(orpc.me.preferences.get.queryKey(SEEN), {
          key: saved.key,
          value: saved.value,
        })
      },
      onError: (error: unknown) => {
        // Personal UI state: the tour has already played; nothing to tell them.
        console.error('[preferences] could not save the tours seen', error)
      },
    }),
  )
}

export function playPageTour(tour: PageTour) {
  const here = `${window.location.pathname}${window.location.search}`
  startTour({
    slug: tour.id,
    title: tour.title,
    kind: 'page',
    returnTo: here,
    // An optional part that isn't on the page now (an empty list) is left out,
    // so "Step 3 of 4" counts only what will show.
    steps: tour.steps
      .filter((step) => !step.optional || findTourTarget(step.target))
      .map((step) => ({ ...step, route: here })),
  })
}

/** Tests and the design audit drive the app in an automated browser: no tour uninvited. */
function mayAutoplay(): boolean {
  if (!navigator.webdriver) return true
  try {
    return window.localStorage.getItem('tours:autoplay') === 'on'
  } catch {
    return false
  }
}

function useCurrentTour(): PageTour | undefined {
  const location = useRouterState({ select: (state) => state.location })
  return tourFor(location.pathname, location.searchStr)
}

/** Mounted once in the signed-in shell. */
export function PageTours({ enabled }: { enabled: boolean }) {
  const tour = useCurrentTour()
  const active = useActiveTour()
  const seen = useToursSeen(enabled)
  // `mutate` keeps its identity across renders; the mutation object doesn't.
  const saveSeen = useSaveToursSeen().mutate
  const started = useRef(new Set<string>())

  useEffect(() => {
    if (!enabled || !tour || active || !seen.isSuccess || !mayAutoplay()) return
    if (seen.data.includes(tour.id) || started.current.has(tour.id)) return
    const first = tour.steps.find((step) => !step.optional) ?? tour.steps[0]
    if (!first) return
    let timer = 0
    const deadline = performance.now() + WAIT_FOR_MS
    const look = () => {
      if (findTourTarget(first.target)) {
        started.current.add(tour.id)
        saveSeen({ key: 'toursSeen', value: [...seen.data, tour.id] })
        playPageTour(tour)
      } else if (performance.now() < deadline) {
        timer = window.setTimeout(look, LOOK_EVERY_MS)
      }
    }
    timer = window.setTimeout(look, LOOK_EVERY_MS)
    return () => window.clearTimeout(timer)
  }, [enabled, tour, active, seen.isSuccess, seen.data, saveSeen])

  const commands = useMemo<Command[]>(
    () =>
      tour
        ? [
            {
              id: 'page-tour',
              label: 'Take the tour of this page',
              group: 'Actions',
              keywords: 'help guide tutorial',
              icon: CircleHelp,
              run: () => playPageTour(tour),
            },
          ]
        : [],
    [tour],
  )
  useRegisterCommands(commands)
  return null
}

/** The top bar's "Take the tour", on a page that has one. */
export function TakeTheTourButton() {
  const tour = useCurrentTour()
  if (!tour) return null
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            aria-label="Take the tour of this page"
            data-tour="take-the-tour"
            onClick={() => playPageTour(tour)}
            className="text-muted-foreground max-md:size-11"
          />
        }
      >
        <CircleHelp aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>Take the tour</TooltipContent>
    </Tooltip>
  )
}
